import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { AnalyticsClient } from "./AnalyticsClient";
import { MockAnalyticsClient } from "./mock/MockAnalyticsClient";
import { HttpAnalyticsClient } from "./HttpAnalyticsClient";
import { API_BASE, API_KEY } from "./config";

const AnalyticsClientContext = createContext<AnalyticsClient | null>(null);

/** The default client: the real gateway when VITE_ANALYTICS_API_BASE is set,
 *  otherwise the deterministic mock. */
export function createDefaultClient(): AnalyticsClient {
  return API_BASE
    ? new HttpAnalyticsClient(API_BASE, API_KEY)
    : new MockAnalyticsClient();
}

export interface AnalyticsProviderProps {
  children: ReactNode;
  /** Inject a client (real HTTP client, or a zero-latency mock in tests). */
  client?: AnalyticsClient;
}

/** Provides the analytics data client to the tree. */
export function AnalyticsProvider({
  children,
  client,
}: AnalyticsProviderProps) {
  const value = useMemo(() => client ?? createDefaultClient(), [client]);
  return (
    <AnalyticsClientContext.Provider value={value}>
      {children}
    </AnalyticsClientContext.Provider>
  );
}

export function useAnalyticsClient(): AnalyticsClient {
  const client = useContext(AnalyticsClientContext);
  if (!client) {
    throw new Error(
      "useAnalyticsClient must be used within an AnalyticsProvider",
    );
  }

  return client;
}
